import { Cross1Icon, IconSize } from '@protoss/ui';
import * as React from 'react';
import { ResizableDivider } from './ResizableDivider';
import './SplitPaneLayout.css';

const DEFAULT_DETAIL_WIDTH = 480;
const MIN_DETAIL_WIDTH = 280;
const MAX_DETAIL_WIDTH = 760;

export interface SplitPaneLayoutProps {
  /** Optional filter bar above the scrollable list (e.g. status filter buttons). */
  filterArea?: React.ReactNode;
  /**
   * Scrollable list area content - table rows, loading indicator, error message, or empty state.
   * The component owns the scrollable wrapper; the caller owns the content.
   */
  children: React.ReactNode;
  /** When true, the pagination bar is hidden. */
  loading?: boolean;
  /** When true, the pagination bar is hidden. */
  hasError?: boolean;
  /** Current 1-based page number. */
  page: number;
  /** Total number of pages. Pagination is hidden when ≤ 1. */
  totalPages: number;
  /** If provided, shown as "N rows · Page X of Y" in the pagination label. */
  rowCount?: number;
  /** Called with the new page number when Prev / Next is clicked. */
  onPageChange: (page: number) => void;
  /** UUID/key of the currently selected item. When null, no detail pane is shown. */
  selectedId: string | null;
  /**
   * Content rendered inside the detail pane header.
   * Placed inside a `flex: 1; overflow: hidden` container - close button is added automatically.
   */
  detailTitle?: React.ReactNode;
  /** Content of the scrollable detail pane body. */
  detailContent?: React.ReactNode;
  /** Called when the user clicks the close button in the detail pane. */
  onCloseDetail: () => void;
  /** Initial width of the detail pane in px. Default: 480. */
  defaultDetailWidth?: number;
  /** Minimum detail pane width in px. Default: 280. */
  minDetailWidth?: number;
  /** Maximum detail pane width in px. Default: 760. */
  maxDetailWidth?: number;
  /** Accessible label for the resizable divider. */
  dividerAriaLabel?: string;
}

/**
 * SplitPaneLayout - shared shell for list + detail pages (Commits, Jobs, etc.)
 *
 * Renders a bordered flex row:
 *   Left:  optional filter bar + scrollable list area + pagination bar
 *   Right: resizable detail pane (only when selectedId is non-null)
 */
export function SplitPaneLayout({
  filterArea,
  children,
  loading = false,
  hasError = false,
  page,
  totalPages,
  rowCount,
  onPageChange,
  selectedId,
  detailTitle,
  detailContent,
  onCloseDetail,
  defaultDetailWidth = DEFAULT_DETAIL_WIDTH,
  minDetailWidth = MIN_DETAIL_WIDTH,
  maxDetailWidth = MAX_DETAIL_WIDTH,
  dividerAriaLabel = 'Resize detail pane',
}: SplitPaneLayoutProps) {
  const [detailWidth, setDetailWidth] = React.useState(defaultDetailWidth);

  const handleDetailResize = React.useCallback(
    (delta: number) => {
      setDetailWidth((prev) => Math.max(minDetailWidth, Math.min(maxDetailWidth, prev - delta)));
    },
    [minDetailWidth, maxDetailWidth]
  );

  const showPagination = totalPages > 1 && !loading && !hasError;

  return (
    <div className="split-pane__shell">
      {/* Left list pane */}
      <div className="split-pane__list-pane">
        {filterArea && <div className="split-pane__filter-area">{filterArea}</div>}
        <div className="split-pane__content-area">{children}</div>
        {showPagination && (
          <div className="split-pane__pagination">
            <span className="split-pane__pag-label">
              {rowCount !== undefined && (
                <>
                  {rowCount} row{rowCount !== 1 ? 's' : ''} &middot;{' '}
                </>
              )}
              Page {page} of {totalPages}
            </span>
            <div className="split-pane__pag-controls">
              <button
                type="button"
                className="split-pane__pag-btn"
                disabled={page <= 1}
                onClick={() => onPageChange(Math.max(1, page - 1))}
                aria-label="Previous page"
              >
                Prev
              </button>
              <button
                type="button"
                className="split-pane__pag-btn"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Resizable divider + detail pane - only when an item is selected */}
      {selectedId !== null && (
        <>
          <ResizableDivider
            direction="vertical"
            onResize={handleDetailResize}
            className="split-pane__divider"
            ariaLabel={dividerAriaLabel}
          />
          <div className="split-pane__detail-pane" style={{ width: detailWidth }}>
            <div className="split-pane__detail-header">
              <div className="split-pane__detail-title">{detailTitle}</div>
              <button
                type="button"
                className="split-pane__detail-close"
                onClick={onCloseDetail}
                aria-label="Close detail pane"
              >
                <Cross1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
              </button>
            </div>
            <div className="split-pane__detail-body">{detailContent}</div>
          </div>
        </>
      )}
    </div>
  );
}
